import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { listTerpenesPublic } from "@/lib/public-substances.functions";
import { buildMeta } from "@/lib/seo";
import { errorMessage } from "@/lib/error-capture";

const terpeneQuery = queryOptions({
  queryKey: ["public-terpenes"],
  queryFn: () => listTerpenesPublic(),
});

export const Route = createFileRoute("/terpene")({
  head: () =>
    buildMeta({
      title: "Terpen-Datenbank",
      description:
        "Terpene aus Cannabis und anderen Pflanzen: pharmakologische Wirkungen, Aromen und Siedepunkte, faktengeprüft.",
      path: "/terpene",
    }),
  loader: ({ context }) => context.queryClient.ensureQueryData(terpeneQuery),
  errorComponent: ({ error }) => (
    <div role="alert" style={{ padding: "2rem", color: "#f87171" }}>
      Fehler: {errorMessage(error)}
    </div>
  ),
  notFoundComponent: () => (
    <div style={{ padding: "2rem" }}>Keine Terpen-Daten gefunden.</div>
  ),
  component: TerpenePage,
});

type Terpene = {
  name: string;
  synonym: string | null;
  terpeneClass: string;
  aroma: string;
  boilingPoint: string;
  boilingPointNote: string | null;
  colorHex: string;
  colorDerived: boolean;
  effect: string;
  evidence: "etabliert" | "präklinisch" | "begrenzt" | "";
  warningNote: string | null;
  mergeNote: string | null;
};

const TH: React.CSSProperties = {
  background: "rgba(168,85,247,0.1)",
  color: "var(--text-secondary)",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.6rem 0.75rem",
  textAlign: "left",
  borderBottom: "1px solid var(--border-purple)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "0.55rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--text-primary)",
  borderBottom: "1px solid rgba(168,85,247,0.08)",
  verticalAlign: "top",
};

function evidenceColor(ev: Terpene["evidence"]): { color: string; label: string } | null {
  if (ev === "etabliert") return { color: "var(--accent-medcan)", label: "etabliert" };
  if (ev === "präklinisch") return { color: "#f2b84b", label: "präklinisch" };
  if (ev === "begrenzt") return { color: "#f87171", label: "begrenzt" };
  return null;
}

function EvidenceBadge({ ev }: { ev: Terpene["evidence"] }) {
  const c = evidenceColor(ev);
  if (!c) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        padding: "0.15rem 0.5rem",
        fontSize: "0.7rem",
        borderRadius: 999,
        border: `1px solid ${c.color}`,
        color: c.color,
        background: `color-mix(in oklab, ${c.color} 12%, transparent)`,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: c.color,
          boxShadow: `0 0 6px ${c.color}`,
        }}
      />
      {c.label}
    </span>
  );
}

function ColorDot({ hex, derived }: { hex: string; derived: boolean }) {
  return (
    <span
      aria-hidden="true"
      title={derived ? "Farbe abgeleitet" : undefined}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: hex || "var(--text-muted)",
        border: derived
          ? `1px dashed color-mix(in oklab, ${hex} 60%, white)`
          : `1px solid color-mix(in oklab, ${hex} 60%, white)`,
        boxShadow: `0 0 8px color-mix(in oklab, ${hex} 55%, transparent)`,
        flexShrink: 0,
      }}
    />
  );
}

function TerpenePage() {
  const { data: terpenes } = useSuspenseQuery(terpeneQuery) as { data: Terpene[] };
  const ALL = "__ALL__";
  const classes = useMemo<string[]>(
    () => [ALL, ...Array.from(new Set(terpenes.map((t) => t.terpeneClass).filter(Boolean)))],
    [terpenes],
  );
  const [cls, setCls] = useState<string>(ALL);
  const [expanded, setExpanded] = useState<string | null>(null);

  type SortKey = "name" | "boiling";
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);

  const extractNum = (s: string | undefined | null): number | null => {
    if (s == null) return null;
    const m = String(s).match(/-?\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };

  const filtered = cls === ALL ? terpenes : terpenes.filter((t) => t.terpeneClass === cls);
  const rows = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.dir === "asc" ? 1 : -1;
    const indexed = filtered.map((t, i) => ({ t, i }));
    indexed.sort((a, b) => {
      let cmp = 0;
      if (sort.key === "name") {
        cmp = a.t.name.localeCompare(b.t.name, "de");
      } else {
        const av = extractNum(a.t.boilingPoint);
        const bv = extractNum(b.t.boilingPoint);
        if (av === null && bv === null) cmp = 0;
        else if (av === null) return 1;
        else if (bv === null) return -1;
        else cmp = av - bv;
      }
      return cmp !== 0 ? cmp * dir : a.i - b.i;
    });
    return indexed.map((x) => x.t);
  }, [filtered, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const SortTH = ({ k, label }: { k: SortKey; label: string }) => {
    const active = sort?.key === k;
    const arrow = active ? (sort!.dir === "asc" ? "▲" : "▼") : "⇅";
    return (
      <th
        style={{ ...TH, cursor: "pointer", userSelect: "none", position: "sticky", top: 0, zIndex: 1 }}
        onClick={() => toggleSort(k)}
        aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}{" "}
        <span style={{ color: active ? "var(--accent)" : "var(--text-muted)", fontSize: "0.75rem" }}>
          {arrow}
        </span>
      </th>
    );
  };

  const dash = (v: string | undefined | null) =>
    v && String(v).trim() !== "" ? (
      v
    ) : (
      <span style={{ color: "var(--text-muted)", opacity: 0.6 }}>-</span>
    );

  return (
    <div data-section="terpene">
      <h1 className="page-title">Terpene</h1>
      <div className="page-title-bar" />

      <p
        style={{
          color: "var(--text-secondary)",
          fontSize: "0.9rem",
          lineHeight: 1.55,
          maxWidth: 760,
          marginBottom: "1.5rem",
        }}
      >
        Terpene aus Cannabis und anderen Pflanzen: pharmakologische Wirkungen, Aromen
        und Siedepunkte, faktengeprüft. Klick auf eine Zeile zeigt Details, Warnhinweise
        und Notizen zur Zusammenführung von Duplikaten.
      </p>

      {classes.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
          {classes.map((c) => {
            const active = c === cls;
            const label = c === ALL ? "Alle" : c;
            return (
              <button
                key={c}
                onClick={() => setCls(c)}
                className={active ? "" : "glass-card"}
                style={{
                  borderRadius: 6,
                  padding: "0.35rem 0.8rem",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  background: active ? "var(--accent)" : undefined,
                  color: active ? "var(--ink-on-accent)" : "var(--text-muted)",
                  border: active ? "1px solid var(--accent)" : undefined,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="glass-card"
        style={{ overflowX: "auto", padding: 0, background: "var(--bg-surface)" }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
          <thead>
            <tr>
              <SortTH k="name" label="Terpen" />
              <th style={{ ...TH, position: "sticky", top: 0, zIndex: 1 }}>Klasse</th>
              <SortTH k="boiling" label="Siedepunkt" />
              <th style={{ ...TH, position: "sticky", top: 0, zIndex: 1 }}>Aroma</th>
              <th style={{ ...TH, position: "sticky", top: 0, zIndex: 1 }}>Wirkung</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <TerpeneRow
                key={t.name}
                t={t}
                open={expanded === t.name}
                onToggle={() => setExpanded((cur) => (cur === t.name ? null : t.name))}
                onClose={() => setExpanded(null)}
                dash={dash}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p
        style={{
          marginTop: "1.5rem",
          color: "var(--text-muted)",
          fontSize: "0.8rem",
          lineHeight: 1.55,
          maxWidth: 780,
        }}
      >
        Wirkungen mit "präklinisch" oder "begrenzt" beruhen auf Tier-, Zell- oder
        Einzelstudien und sind nicht klinisch am Menschen belegt. Kein Ersatz für
        medizinische Beratung. Aus 37 Roheinträgen wurden 31 eindeutige Terpene
        (Duplikate zusammengeführt).
      </p>
    </div>
  );
}

function TerpeneRow({
  t,
  open,
  onToggle,
  onClose,
  dash,
}: {
  t: Terpene;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  dash: (v: string | undefined | null) => React.ReactNode;
}) {
  const reactId = useId();
  const triggerId = `terpene-trigger-${reactId}`;
  const detailId = `terpene-detail-${reactId}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const prevOpen = useRef(open);

  useEffect(() => {
    if (open && !prevOpen.current) {
      detailRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleDetailKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  return (
    <Fragment>
      <tr className="terpene-row" data-open={open ? "true" : "false"}>
        <td style={{ ...TD, fontWeight: 600, minWidth: 180 }}>
          <button
            ref={triggerRef}
            type="button"
            id={triggerId}
            className="terpene-row-trigger"
            aria-expanded={open}
            aria-controls={detailId}
            onClick={onToggle}
          >
            <ColorDot hex={t.colorHex} derived={t.colorDerived} />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span>
                {t.warningNote && (
                  <>
                    <AlertTriangle
                      size={13}
                      style={{ color: "#f87171", marginRight: 4, verticalAlign: "-2px" }}
                      aria-hidden="true"
                    />
                    <span className="sr-only">Warnhinweis. </span>
                  </>
                )}
                {t.name}
              </span>
              {t.synonym && (
                <span
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "0.7rem",
                    fontFamily:
                      '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
                    fontWeight: 400,
                  }}
                >
                  {t.synonym}
                </span>
              )}
            </span>
          </button>
        </td>
        <td style={{ ...TD, color: "var(--text-secondary)" }}>{dash(t.terpeneClass)}</td>
        <td style={TD}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
            <span>
              {t.boilingPoint ? `${t.boilingPoint} °C` : (
                <span style={{ color: "var(--text-muted)", opacity: 0.6 }}>-</span>
              )}
            </span>
            {t.boilingPointNote && (
              <button
                type="button"
                onClick={onToggle}
                aria-label={`Siedepunkt-Hinweis: ${t.boilingPointNote}`}
                aria-expanded={open}
                aria-controls={detailId}
                title={t.boilingPointNote}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 2,
                  cursor: "pointer",
                  color: "var(--accent)",
                  display: "inline-flex",
                }}
              >
                <Info size={13} aria-hidden="true" />
              </button>
            )}
          </span>
        </td>
        <td style={{ ...TD, color: "var(--text-secondary)" }}>{dash(t.aroma)}</td>
        <td style={TD}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
            <EvidenceBadge ev={t.evidence} />
            <span style={{ flex: 1, minWidth: 200 }}>{t.effect}</span>
          </div>
        </td>
      </tr>
      {open && (
        <tr onKeyDown={handleDetailKeyDown}>
          <td
            colSpan={5}
            style={{ background: "rgba(26,16,51,0.9)", padding: "1rem 0.75rem" }}
          >
            <div
              id={detailId}
              ref={detailRef}
              role="region"
              aria-labelledby={triggerId}
              tabIndex={-1}
              style={{ display: "flex", flexDirection: "column", gap: "0.75rem", outline: "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <EvidenceBadge ev={t.evidence} />
                <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                  Klasse: <span style={{ color: "var(--text-primary)" }}>{dash(t.terpeneClass)}</span>
                </span>
              </div>
              <p style={{ color: "var(--text-primary)", fontSize: "0.9rem", lineHeight: 1.55, margin: 0 }}>
                {t.effect || "Noch keine Daten hinterlegt."}
              </p>
              {t.boilingPointNote && (
                <div style={{ color: "var(--text-muted)", fontSize: "0.8rem", lineHeight: 1.5 }}>
                  <span style={{ color: "var(--accent)" }}>Siedepunkt-Hinweis: </span>
                  {t.boilingPointNote}
                </div>
              )}
              {t.warningNote && (
                <div
                  role="note"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.5rem",
                    padding: "0.6rem 0.75rem",
                    border: "1px solid color-mix(in oklab, #f87171 55%, transparent)",
                    background: "color-mix(in oklab, #f87171 10%, transparent)",
                    borderRadius: 8,
                    fontSize: "0.82rem",
                    lineHeight: 1.5,
                    color: "var(--text-primary)",
                  }}
                >
                  <AlertTriangle
                    size={16}
                    style={{ color: "#f87171", flexShrink: 0, marginTop: 2 }}
                    aria-hidden="true"
                  />
                  <span>
                    <span className="sr-only">Warnhinweis: </span>
                    {t.warningNote}
                  </span>
                </div>
              )}
              {t.mergeNote && (
                <div style={{ color: "var(--text-muted)", fontSize: "0.78rem", fontStyle: "italic" }}>
                  Zusammengeführt: {t.mergeNote}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}
